"use client";

import Image from "next/image";
import React from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import VerifiedTick from "@/components/reviews/VerifiedTick";
import { urlForImage } from "@/sanity/lib/image";
import type { ReviewSource } from "@/app/(client)/reviews/page";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

interface Review {
  _id: string;
  title?: string;
  context?: string;
  tags?: string[];
  stars?: number;
  source?: string;
  verified?: boolean;
  name?: string;
  avatar?: { asset?: { _ref?: string }; _type?: string };
  avatarUrl?: string;
  sourceUrl?: string;
}

function StarsDisplay({ stars }: { stars: number }) {
  const n = Math.min(5, Math.max(0, stars));
  return (
    <span className="inline-flex text-amber-400" aria-label={`${n} stars`}>
      {"★".repeat(n)}
      {"☆".repeat(5 - n)}
    </span>
  );
}

interface ReviewDetailModalProps {
  review: Review | null;
  reviewSources: ReviewSource[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ReviewDetailModal({
  review,
  reviewSources,
  open,
  onOpenChange,
}: ReviewDetailModalProps) {
  if (!review) return null;

  const avatarSrc = review.avatar
    ? urlForImage(review.avatar).width(80).height(80).url()
    : (review.avatarUrl ?? null);
  const avatarIsExternal = Boolean(review.avatarUrl && !review.avatar);
  const sourceConfig = review.source
    ? reviewSources.find(
        (s) => slugify(s.name) === review.source?.toLowerCase()
      )
    : null;
  const sourceLogoUrl =
    sourceConfig?.logo &&
    urlForImage(sourceConfig.logo).width(28).height(28).url();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-violet-200 bg-white p-6 sm:rounded-xl">
        <DialogTitle className="sr-only">
          Full review by {review.name}
        </DialogTitle>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            {avatarSrc ? (
              <div className="relative size-14 shrink-0 overflow-hidden rounded-full bg-violet-100">
                <Image
                  src={avatarSrc}
                  alt=""
                  width={56}
                  height={56}
                  className="object-cover"
                  unoptimized={avatarIsExternal}
                />
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <StarsDisplay stars={review.stars ?? 0} />
                {sourceLogoUrl ? (
                  <span
                    className="relative flex size-7 shrink-0"
                    aria-label={sourceConfig?.name}
                  >
                    <Image
                      src={sourceLogoUrl}
                      alt=""
                      width={28}
                      height={28}
                      className="object-contain"
                    />
                  </span>
                ) : null}
                {review.verified && <VerifiedTick />}
              </div>
              <p className="mt-1 font-semibold text-violet-950">
                {review.name}
              </p>
            </div>
          </div>
          {review.title && (
            <h3 className="text-lg font-semibold text-violet-950">
              {review.title}
            </h3>
          )}
          <p className="text-violet-800 whitespace-pre-wrap">
            {review.context}
          </p>
          {review.tags && review.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {review.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          {review.sourceUrl && (
            <a
              href={review.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100"
            >
              View on source
              <svg
                className="size-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
